/**
 * Lectura GSPS (PR) con dicom-parser: referencias SOP + Graphic Annotation Sequence.
 */
import * as dicomParser from 'dicom-parser'

export const GSPS_SOP_CLASS = '1.2.840.10008.5.1.4.1.1.11.1'
export const GSPS_XA_SOP_CLASS = '1.2.840.10008.5.1.4.1.1.11.5'

function seqItems(ds, tag) {
  if (!ds || !ds.elements) return []
  const el = ds.elements[tag]
  if (!el || !Array.isArray(el.items)) return []
  return el.items.map((it) => it.dataSet || it).filter(Boolean)
}

function str(ds, tag) {
  if (!ds || typeof ds.string !== 'function') return null
  try {
    const v = ds.string(tag)
    return v != null && String(v).trim() ? String(v).trim() : null
  } catch (e) {
    return null
  }
}

function uint(ds, tag) {
  if (!ds) return null
  try {
    if (typeof ds.uint16 === 'function') {
      const n = ds.uint16(tag)
      if (n != null && Number.isFinite(n)) return n
    }
    if (typeof ds.uint32 === 'function') {
      const n = ds.uint32(tag)
      if (n != null && Number.isFinite(n)) return n
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

export function readFloatArray(ds, tag) {
  if (!ds || !ds.elements) return []
  const el = ds.elements[tag]
  if (!el || !(el.length > 0)) return []
  const n = Math.floor(el.length / 4)
  const out = []
  for (let i = 0; i < n; i++) {
    try {
      out.push(ds.float(tag, i))
    } catch (e) {
      break
    }
  }
  return out.filter((v) => Number.isFinite(v))
}

export function isGspsDataset(ds) {
  const sop = str(ds, 'x00080016') || ''
  const mod = (str(ds, 'x00080060') || '').toUpperCase()
  return (
    sop === GSPS_SOP_CLASS ||
    sop === GSPS_XA_SOP_CLASS ||
    mod === 'PR'
  )
}

export function collectReferencedSopUids(ds) {
  const uids = []
  const push = (v) => {
    if (v && uids.indexOf(v) < 0) uids.push(v)
  }
  seqItems(ds, 'x00081115').forEach((series) => {
    seqItems(series, 'x00081140').forEach((img) => {
      push(str(img, 'x00081155'))
    })
  })
  seqItems(ds, 'x00081140').forEach((img) => {
    push(str(img, 'x00081155'))
  })
  seqItems(ds, 'x00700001').forEach((ann) => {
    seqItems(ann, 'x00081140').forEach((img) => {
      push(str(img, 'x00081155'))
    })
  })
  return uids
}

function pairsToPoints(floats) {
  const pts = []
  for (let i = 0; i + 1 < floats.length; i += 2) {
    pts.push({ x: floats[i], y: floats[i + 1] })
  }
  return pts
}

export function parseGraphicObjects(ds) {
  const graphics = []
  seqItems(ds, 'x00700001').forEach((ann, ai) => {
    const layer = str(ann, 'x00700002') || `LAYER${ai}`
    seqItems(ann, 'x00700009').forEach((go, gi) => {
      const type = (str(go, 'x00700023') || '').toUpperCase()
      const nPts = uint(go, 'x00700021')
      const data = readFloatArray(go, 'x00700022')
      const points = pairsToPoints(data)
      graphics.push({
        layer,
        graphicType: type,
        numberOfPoints: nPts != null ? nPts : points.length,
        points,
        filled: (str(go, 'x00700024') || '') === 'Y',
        index: gi,
      })
    })
    seqItems(ann, 'x00700008').forEach((tx) => {
      graphics.push({
        layer,
        graphicType: 'TEXT',
        text: str(tx, 'x00700006') || str(tx, 'x00700003') || '',
        points: [],
        index: -1,
      })
    })
  })
  return graphics
}

/** Primer valor DS (Window Center/Width pueden ser multi-valor). */
function firstDsNumber(ds, tag) {
  const raw = str(ds, tag)
  if (!raw) return null
  const first = String(raw).split('\\')[0].trim()
  const n = Number(first)
  return Number.isFinite(n) ? n : null
}

/**
 * Softcopy VOI LUT Sequence (0028,3110) + fallback WW/WC en raíz del PR.
 * Presets de consola (WW/WC) tal como se guardaron en el GSPS.
 *
 * @param {object} ds
 * @returns {Array<{ windowWidth: number, windowCenter: number, referencedSopUids: string[] }>}
 */
export function parseSoftcopyVoiEntries(ds) {
  const out = []
  const push = (item, refUids) => {
    if (!item) return
    const ww = firstDsNumber(item, 'x00281051')
    const wc = firstDsNumber(item, 'x00281050')
    if (ww == null || wc == null || !(ww > 0)) return
    out.push({
      windowWidth: ww,
      windowCenter: wc,
      referencedSopUids: Array.isArray(refUids) ? refUids.filter(Boolean) : [],
    })
  }

  seqItems(ds, 'x00283110').forEach((item) => {
    const refs = []
    seqItems(item, 'x00081140').forEach((img) => {
      const uid = str(img, 'x00081155')
      if (uid) refs.push(uid)
    })
    push(item, refs)
  })

  if (!out.length) {
    push(ds, [])
  }
  return out
}

/**
 * @param {object} ds dicom-parser DataSet
 * @returns {{
 *   isGsps: boolean,
 *   sopInstanceUID: string|null,
 *   modality: string,
 *   referencedSopUids: string[],
 *   graphics: object[],
 *   softcopyVoi: Array<{ windowWidth: number, windowCenter: number, referencedSopUids: string[] }>,
 * }}
 */
export function parseGspsDataset(ds) {
  if (!ds) {
    return {
      isGsps: false,
      sopInstanceUID: null,
      modality: '',
      referencedSopUids: [],
      graphics: [],
      softcopyVoi: [],
    }
  }
  return {
    isGsps: isGspsDataset(ds),
    sopInstanceUID: str(ds, 'x00080018'),
    modality: (str(ds, 'x00080060') || '').toUpperCase(),
    referencedSopUids: collectReferencedSopUids(ds),
    graphics: parseGraphicObjects(ds),
    softcopyVoi: parseSoftcopyVoiEntries(ds),
  }
}

export function parseGspsFromImage(image) {
  return parseGspsDataset(image && image.data)
}

export function parseGspsFromBytes(byteArray) {
  if (!byteArray) return parseGspsDataset(null)
  const ds = dicomParser.parseDicom(byteArray)
  return parseGspsDataset(ds)
}
